import { useState, useRef } from 'react';
import { extractTextFromPDF, isPDF } from '../utils/pdfParser';
import { api } from '../utils/api';
import { CATEGORIES, formatDate } from '../utils/helpers';

export function UploadView({ onAddItems, showToast }) {
  const [tab, setTab] = useState('upload'); // upload, manual
  const [extractedText, setExtractedText] = useState('');
  const [manualText, setManualText] = useState('');
  const [parsedItems, setParsedItems] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [dragover, setDragover] = useState(false);
  const fileInputRef = useRef(null);

  // Datei verarbeiten
  const processFile = async (file) => {
    try {
      let text;
      if (isPDF(file)) {
        showToast('PDF wird verarbeitet...');
        text = await extractTextFromPDF(file);
      } else if (file.type.startsWith('image/')) {
        showToast('Für Bilder bitte den Text manuell eingeben');
        setTab('manual');
        return;
      } else {
        // Text-Datei
        text = await file.text();
      }

      if (!text || text.trim().length < 10) {
        showToast('Konnte keinen Text extrahieren. Versuche den manuellen Modus.');
        setTab('manual');
        return;
      }

      setExtractedText(text);
      await parseReceiptText(text);
    } catch (err) {
      console.error('Fehler bei der Dateiverarbeitung:', err);
      showToast('Fehler beim Lesen der Datei. Versuche den manuellen Modus.');
      setTab('manual');
    }
  };

  // Text an AI senden zum Parsen
  const parseReceiptText = async (text) => {
    setParsing(true);
    try {
      const data = await api.parseReceipt(text);
      if (data.items && data.items.length > 0) {
        setParsedItems(data.items.map(item => ({ ...item, selected: true })));
        showToast(`${data.items.length} Produkte erkannt!`);
      } else {
        showToast('Keine Produkte erkannt. Prüfe den Text.');
      }
    } catch (err) {
      showToast('Fehler beim Parsen: ' + err.message);
    } finally {
      setParsing(false);
    }
  };

  // Datei-Upload Handler
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) processFile(file);
  };

  // Drag & Drop
  const handleDrop = (e) => {
    e.preventDefault();
    setDragover(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  // Manuellen Text parsen
  const handleManualParse = () => {
    if (!manualText.trim()) return;
    parseReceiptText(manualText.trim());
  };

  // Item an/abwählen
  const toggleItem = (index) => {
    setParsedItems(prev => prev.map((item, i) =>
      i === index ? { ...item, selected: !item.selected } : item
    ));
  };

  // Item entfernen
  const removeItem = (index) => {
    setParsedItems(prev => prev.filter((_, i) => i !== index));
  };

  // Ausgewählte Items zum Kühlschrank hinzufügen
  const handleAddSelected = () => {
    const selected = parsedItems.filter(i => i.selected);
    if (selected.length === 0) {
      showToast('Keine Produkte ausgewählt');
      return;
    }
    onAddItems(selected);
    setParsedItems([]);
    setExtractedText('');
    setManualText('');
  };

  return (
    <div className="content">
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Kassenbeleg hinzufügen</h2>

      {/* Tab Switcher */}
      <div className="tabs">
        <button
          className={`tab ${tab === 'upload' ? 'active' : ''}`}
          onClick={() => setTab('upload')}
        >
          📄 PDF / Bild
        </button>
        <button
          className={`tab ${tab === 'manual' ? 'active' : ''}`}
          onClick={() => setTab('manual')}
        >
          ✏️ Text einfügen
        </button>
      </div>

      {/* Upload Tab */}
      {tab === 'upload' && !parsedItems.length && (
        <>
          <div
            className={`upload-area ${dragover ? 'dragover' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragover(true); }}
            onDragLeave={() => setDragover(false)}
            onDrop={handleDrop}
          >
            <div className="upload-icon">📄</div>
            <div className="upload-text">
              Kassenbeleg hochladen
            </div>
            <div className="upload-hint">
              PDF von Rewe oder Foto vom Beleg
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          {extractedText && (
            <div style={{ marginTop: 16 }}>
              <div className="settings-label">Extrahierter Text:</div>
              <textarea
                className="textarea"
                value={extractedText}
                onChange={(e) => setExtractedText(e.target.value)}
                rows={6}
              />
              <button
                className="btn btn-primary btn-block"
                style={{ marginTop: 8 }}
                onClick={() => parseReceiptText(extractedText)}
                disabled={parsing}
              >
                {parsing ? 'Wird analysiert...' : 'Erneut analysieren'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Manual Tab */}
      {tab === 'manual' && !parsedItems.length && (
        <div>
          <p style={{ fontSize: 14, color: 'var(--gray-500)', marginBottom: 12 }}>
            Füge den Kassenbeleg-Text hier ein. Du kannst z.B. den Text aus der Lidl-App kopieren
            oder den Beleg abfotografieren und den erkannten Text einfügen.
          </p>
          <textarea
            className="textarea"
            placeholder={'REWE\nVollmilch 3,5%  1,19\nBio Eier 10St  3,29\nGemischtes Hack  4,99\n...'}
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            rows={8}
            autoFocus
          />
          <button
            className="btn btn-primary btn-block"
            style={{ marginTop: 12 }}
            onClick={handleManualParse}
            disabled={!manualText.trim() || parsing}
          >
            {parsing ? 'Wird analysiert...' : '🔍 Produkte erkennen'}
          </button>
        </div>
      )}

      {/* Loading */}
      {parsing && (
        <div className="loading-overlay">
          <div className="spinner" />
          <span>AI analysiert den Beleg...</span>
        </div>
      )}

      {/* Parsed Items Vorschau */}
      {parsedItems.length > 0 && (
        <div className="parsed-items">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>
              Erkannte Produkte ({parsedItems.filter(i => i.selected).length}/{parsedItems.length})
            </h3>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => { setParsedItems([]); setExtractedText(''); setManualText(''); }}
            >
              Zurück
            </button>
          </div>

          {parsedItems.map((item, index) => {
            const cat = CATEGORIES[item.category] || CATEGORIES.sonstiges;
            return (
              <div
                key={index}
                className={`parsed-item ${!item.selected ? 'deselected' : ''}`}
                style={{ opacity: item.selected ? 1 : 0.5 }}
              >
                <div
                  className={`item-checkbox ${item.selected ? 'checked' : ''}`}
                  onClick={() => toggleItem(index)}
                  style={{ marginRight: 10 }}
                >
                  {item.selected && '✓'}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="parsed-item-name">
                    {cat.icon} {item.name}
                  </div>
                  <div className="parsed-item-expiry">
                    {item.expiry_date ? `MHD: ${formatDate(item.expiry_date)}` : 'Kein MHD geschätzt'}
                    {item.quantity > 1 && ` · ${item.quantity} ${item.unit || 'Stk'}`}
                  </div>
                </div>
                <button
                  className="item-action-btn"
                  onClick={() => removeItem(index)}
                  style={{ fontSize: 14 }}
                >
                  ✕
                </button>
              </div>
            );
          })}

          <button
            className="btn btn-success btn-block"
            style={{ marginTop: 16 }}
            onClick={handleAddSelected}
          >
            ✓ {parsedItems.filter(i => i.selected).length} Produkte zum Kühlschrank hinzufügen
          </button>
        </div>
      )}
    </div>
  );
}
